import { useCallback, useLayoutEffect, useState } from 'react';

// Positions a floating overlay (tooltip, popover) against a trigger with
// `position: fixed`, so no scroll/overflow ancestor can clip it — the same
// escape hatch useFixedDropdownPosition uses for menus.
//
// The geometry is what differs, which is why this is a second hook rather than
// an option on that one: a dropdown is LEFT-ALIGNED to its trigger, matches its
// width and caps its height; an overlay has its own intrinsic size, wants to be
// CENTRED, and must be pushed back inside the viewport when centring would
// overflow it. floating-ui calls these shift() and flip().
//
// TWO-PASS. The caller renders the floating element with the style returned on
// the first render — a hidden probe at (0,0) — and this hook's layout effect
// measures it and writes the real coordinates BEFORE the browser paints, so the
// probe position is never visible. Measuring is required, not optional: a
// `width: max-content; max-width: N` bubble renders at min(intrinsic, N), and
// assuming N would push every short tip visibly off-centre from its trigger.
// The probe is valid because max-content + a FIXED max-width is
// position-independent — do not switch that cap to anything that depends on
// `left` (e.g. calc(100vw - left)) or the measurement stops predicting the
// final layout.
//
// Caveat inherited from `position: fixed`: an ancestor with a non-none
// transform / filter / perspective / will-change / contain becomes the
// containing block and this silently positions against it instead of the
// viewport. No current host does (the modals are plain flex overlays), but a
// React Flow custom node body would — `.react-flow__viewport` is transformed.
//
// Visuals stay at the call site; this hook only positions.

const MARGIN = 8; // keep-out inset from every viewport edge

// Frozen so the `!open` reset is an Object.is no-op and never re-renders.
const PROBE = Object.freeze({ position: 'fixed', top: 0, left: 0, visibility: 'hidden' });

export default function useAnchoredPosition(triggerRef, floatingRef, open, { place = 'top', gap = 8 } = {}) {
  const [style, setStyle] = useState(PROBE);

  const calc = useCallback(() => {
    const trigger = triggerRef.current;
    const floating = floatingRef.current;
    // Unmounted, or closed between the event and this callback — a queued
    // capture-phase scroll callback lands here. Bail rather than throw.
    if (!trigger || !floating) return;

    const anchor = trigger.getBoundingClientRect();
    const box = floating.getBoundingClientRect();
    // clientWidth/Height exclude scrollbars (innerWidth/Height do not, and
    // clamping to those would let the overlay slide under a scrollbar). The
    // `||` fallback is load-bearing under jsdom, which reports 0 because it
    // performs no layout.
    const vw = document.documentElement.clientWidth || window.innerWidth || 0;
    const vh = document.documentElement.clientHeight || window.innerHeight || 0;

    // The trigger scrolled off-screen. An absolute bubble used to scroll away
    // with it; a fixed one would stay pinned, pointing at nothing.
    if (anchor.bottom < 0 || anchor.right < 0 || anchor.top > vh || anchor.left > vw) {
      setStyle(PROBE);
      return;
    }

    // shift(): centre on the trigger, then slide back inside the inset box.
    // Math.max applied LAST is what makes an overlay wider than the viewport
    // pin to the left inset instead of running off the left edge.
    const centred = anchor.left + anchor.width / 2 - box.width / 2;
    const left = Math.max(MARGIN, Math.min(centred, vw - MARGIN - box.width));

    // flip(): `place` is a preference. It loses only when the preferred side
    // cannot fit the overlay and the other side can; if neither fits, the
    // roomier side wins.
    const roomAbove = anchor.top - gap - MARGIN;
    const roomBelow = vh - anchor.bottom - gap - MARGIN;
    const prefersTop = place !== 'bottom';
    const preferred = prefersTop ? roomAbove : roomBelow;
    const alternate = prefersTop ? roomBelow : roomAbove;
    const onTop = preferred >= box.height
      ? prefersTop
      : alternate >= box.height
        ? !prefersTop
        : roomAbove > roomBelow;

    setStyle({
      position: 'fixed',
      left: Math.round(left),
      // Anchor the edge FACING the trigger (bottom when above, top when below)
      // so the gap stays exact if the overlay re-wraps to a different height.
      ...(onTop
        ? { bottom: Math.round(vh - anchor.top + gap), top: 'auto' }
        : { top: Math.round(anchor.bottom + gap), bottom: 'auto' }),
      visibility: 'visible',
    });
  }, [triggerRef, floatingRef, place, gap]);

  // Measure before paint, then follow the trigger. The scroll listener is
  // capture-phase because scroll events don't bubble — this is how scrolls of a
  // modal body (not just the window) are caught. Closing resets to the probe so
  // the next open re-measures instead of flashing the previous position, and
  // the cleanup runs on unmount-while-open, so no listener can fire against a
  // dead component.
  useLayoutEffect(() => {
    if (!open) { setStyle(PROBE); return undefined; }
    calc();
    window.addEventListener('scroll', calc, true);
    window.addEventListener('resize', calc);
    return () => {
      window.removeEventListener('scroll', calc, true);
      window.removeEventListener('resize', calc);
    };
  }, [open, calc]);

  return style;
}
